import { useEffect, ReactNode } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { isExpaiApiEnabled } from '@/integrations/api/config';
import { fetchTenantBranding } from '@/integrations/api/platform_api';
import { supabase } from '@/integrations/supabase/client';

interface OrgBranding {
  primary: string;
  secondary: string;
  accent: string;
  fontFamily: string;
}

const DEFAULTS: Record<string, OrgBranding> = {
  acme: { primary: '#1F3864', secondary: '#C9A84C', accent: '#C9A84C', fontFamily: "'Inter', system-ui, sans-serif" },
  beta: { primary: '#16A34A', secondary: '#F97316', accent: '#F97316', fontFamily: "'Inter', system-ui, sans-serif" },
  gamma: { primary: '#7C3AED', secondary: '#06B6D4', accent: '#06B6D4', fontFamily: "'Inter', system-ui, sans-serif" },
  demo: { primary: '#1F3864', secondary: '#C9A84C', accent: '#C9A84C', fontFamily: "'Inter', system-ui, sans-serif" },
};

const FALLBACK: OrgBranding = {
  primary: '#1F3864',
  secondary: '#C9A84C',
  accent: '#C9A84C',
  fontFamily: "'Inter', system-ui, sans-serif",
};

function apply(branding: OrgBranding) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--color-primary', branding.primary);
  root.style.setProperty('--color-secondary', branding.secondary);
  root.style.setProperty('--color-accent', branding.accent);
  root.style.setProperty('--font-family', branding.fontFamily);
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { currentOrg } = useTenant();

  useEffect(() => {
    let cancelled = false;
    const slug = currentOrg?.slug ?? '';
    const fallback = DEFAULTS[slug] ?? FALLBACK;

    (async () => {
      if (!currentOrg) {
        apply(FALLBACK);
        return;
      }

      let branding: OrgBranding = fallback;

      try {
        if (isExpaiApiEnabled()) {
          const row = await fetchTenantBranding(currentOrg.id);
          branding = {
            primary: row.primary_color ?? fallback.primary,
            secondary: row.secondary_color ?? fallback.secondary,
            accent: row.accent_color ?? fallback.accent,
            fontFamily: row.font_family ?? fallback.fontFamily,
          };
        } else {
          const { data, error } = await supabase
            .from('tenants_branding')
            .select('primary_color, secondary_color, accent_color, font_family')
            .eq('tenant_id', currentOrg.id)
            .maybeSingle();
          if (!error && data) {
            const row = data as {
              primary_color?: string | null;
              secondary_color?: string | null;
              accent_color?: string | null;
              font_family?: string | null;
            };
            branding = {
              primary: row.primary_color ?? fallback.primary,
              secondary: row.secondary_color ?? fallback.secondary,
              accent: row.accent_color ?? fallback.accent,
              fontFamily: row.font_family ?? fallback.fontFamily,
            };
          }
        }
      } catch {
        // keep fallback
      }

      if (!cancelled) apply(branding);
    })();

    return () => {
      cancelled = true;
    };
  }, [currentOrg]);

  return <>{children}</>;
}
